import React, { useEffect, useState } from "react";
import { useStore } from "react-redux";
import { fetchPatientByReference } from "../services/patientService";
import { fetchProviderByReference } from "../services/providerService";
import { fetchEncounterByReference } from "../services/encounterService";
import { fetchOrganizationByReference } from "../services/organizationService";
import styles from "../modals/ResourceViewerModal.module.css";
import localStyles from "./GoalCardExtended.module.css";
import { formatLocalDate } from "../utils/dateUtils";
import CardHeader from "./CardHeader";

const SectionHeader = ({ children }) => (
    <div className={localStyles.sectionHeader}>{children}</div>
);

const Field = ({ label, children }) => (
    <div className="mb-1">
        <strong>{label}:</strong>{" "}
        {children || <span className="text-muted">N/A</span>}
    </div>
);

const renderCodeable = (cc) => {
    if (!cc) return <span className="text-muted">N/A</span>;
    if (cc.coding?.length) {
        return (
            <>
                {cc.coding.map((c, idx) => (
                    <div key={idx} className="text-muted small ps-3">
                        {c.system ? `${c.system} → ` : ""}
                        {c.code} — {c.display || c.code}
                    </div>
                ))}
            </>
        );
    }
    if (cc.text) return <div>{cc.text}</div>;
    return <span className="text-muted">N/A</span>;
};

const GoalCardExtended = ({ resource, handleShowNested, fhirClient }) => {
    const store = useStore();
    const [subject, setSubject] = useState(null);
    const [author, setAuthor] = useState(null);
    const [organization, setOrganization] = useState(null);
    const [encounterData, setEncounterData] = useState(null);

    /** Fetch references */
    useEffect(() => {
        if (!resource || !fhirClient) return;

        const fetchRefs = async () => {
            try {
                if (resource.subject?.reference) {
                    const p = await fetchPatientByReference(
                        resource.subject.reference,
                        fhirClient,
                        store
                    );
                    setSubject(p);
                }

                const authorRef =
                    resource.expressedBy?.reference ||
                    resource.author?.reference;
                if (authorRef) {
                    const a = await fetchProviderByReference(
                        authorRef,
                        fhirClient,
                        store
                    );
                    setAuthor(a);
                }

                if (resource.encounter?.reference) {
                    const e = await fetchEncounterByReference(
                        resource.encounter.reference,
                        fhirClient,
                        store
                    );
                    setEncounterData(e);
                }

                if (resource.source?.reference) {
                    const o = await fetchOrganizationByReference(
                        resource.source.reference,
                        fhirClient,
                        store
                    );
                    setOrganization(o);
                }
            } catch (err) {
                console.error("GoalCardExtended: failed to fetch references", err);
            }
        };

        fetchRefs();
    }, [resource, fhirClient, store]);

    return (
        <div className="p-4 border rounded shadow-sm bg-white">
            <CardHeader resource={resource} handleShowNested={handleShowNested} fhirClient={fhirClient} />

            {/* IDENTIFICATION */}
            <SectionHeader>Identification</SectionHeader>
            <div className={styles.indented}>
                <div className={styles.sectionContent}>
                    <Field label="Goal ID">{resource.id}</Field>
                    <Field label="Lifecycle Status">
                        {resource.lifecycleStatus}
                    </Field>
                    <Field label="Achievement Status">
                        {renderCodeable(resource.achievementStatus)}
                    </Field>
                    <Field label="Priority">
                        {renderCodeable(resource.priority)}
                    </Field>
                </div>
            </div>

            {/* DESCRIPTION */}
            <SectionHeader>Description</SectionHeader>
            <div className={styles.indented}>
                <Field label="Description">
                    {resource.description?.text}
                </Field>
                <Field label="Category">
                    {renderCodeable(resource.category?.[0])}
                </Field>
            </div>

            {/* TARGETS */}
            {resource.target?.length > 0 && (
                <>
                    <SectionHeader>Targets</SectionHeader>
                    <div className={styles.indented}>
                        {resource.target.map((t, idx) => (
                            <div
                                key={idx}
                                className="mb-2 border rounded p-2 bg-light"
                            >
                                <Field label="Measure">
                                    {renderCodeable(t.measure)}
                                </Field>
                                <Field label="Due Date">
                                    {formatLocalDate(t.dueDate)}
                                </Field>
                                <Field label="Detail">
                                    {t.detailQuantity
                                        ? `${t.detailQuantity.value} ${t.detailQuantity.unit}`
                                        : t.detailString}
                                </Field>
                            </div>
                        ))}
                    </div>
                </>
            )}

            {/* ADDRESSES / OUTCOMES */}
            {(resource.addresses?.length ||
                resource.outcomeReference?.length) && (
                <>
                    <SectionHeader>Clinical Context</SectionHeader>
                    <div className={styles.indented}>
                        <Field label="Addresses">
                            {resource.addresses?.map((a, idx) => (
                                <div key={idx}>
                                    {a.display || a.reference}
                                </div>
                            ))}
                        </Field>
                        <Field label="Outcomes">
                            {resource.outcomeReference?.map((o, idx) => (
                                <div key={idx}>
                                    {o.display || o.reference}
                                </div>
                            ))}
                        </Field>
                    </div>
                </>
            )}

            {/* METADATA */}
            <SectionHeader>Metadata</SectionHeader>
            <div className={styles.indented}>
                <Field label="FHIR Resource Type">
                    {resource.resourceType}
                </Field>
                <Field label="Version">
                    {resource.meta?.versionId}
                </Field>
                <Field label="Last Updated">
                    {resource.meta?.lastUpdated
                        ? formatLocalDate(resource.meta.lastUpdated)
                        : "N/A"}
                </Field>
            </div>
        </div>
    );
};

export default GoalCardExtended;
